import Cookies from 'js-cookie';
import { Button, Heading, Text } from '@livechat/design-system-react-components';

import Config from 'Config';
import { Product } from 'types/product';
import { Logo } from 'components/logo/Logo';
import { getProductDisplayName, getProductName } from 'helpers/product';
import { useProducts } from 'hooks/use-products';
import { useSharedStore } from 'stores/shared-store';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { saveItem } from 'helpers/storage/local-storage';
import Amplitude from 'services/Amplitude';

import {
  productCardButtonStyles,
  productCardContainerStyles,
  productCopyContainerStyles,
  productLogoContainerStyles,
} from './styles';

type Props = {
  product: Product;
  isLoggedIn: boolean;
  fullWidth?: boolean;
};

export const TrialProductCard = ({ product, isLoggedIn, fullWidth }: Props) => {
  const { products, isFetchingProducts } = useProducts();
  let copy: string;

  const defaultParams = {
    source_id: 'create_account_link',
    source_url: window.location.origin,
    source_type: 'accounts',
  };

  const sharedStore = useSharedStore();
  sharedStore.addMissing(defaultParams);

  const sharedStoreParams = sharedStore.toObject();

  const queryString = Object.keys(sharedStoreParams)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(sharedStoreParams[key].toString())}`
    )
    .join('&');

  const email = sharedStore.get('email');
  const productName = getProductName(product);

  const handleClick = async () => {
    Cookies.set('product', product, { expires: 365, sameSite: 'Lax' });
    const params = products.find((productDetails) => productDetails.product === productName);
    Amplitude.logEvent(email?.toString(), 'START_TRIAL_ITEM_CLICK', { product: productName }, true);

    if (params?.entrypoint && isLoggedIn) {
      saveItem('signup', '1');
      window.location.href = `${params.entrypoint}?${queryString}`;
    } else if (params?.entrypoint && !isLoggedIn) {
      window.location.href = `${params.entrypoint}?callback=/signup&${queryString}`;
    } else if (isLoggedIn) {
      saveItem('signup', '1');
      window.location.href = `${Config.accounts_api_url}?${buildOauthQuery(params)}&${queryString}`;
    } else {
      window.location.href = email
        ? `/signup?email=${encodeURIComponent(email as string)}&${queryString}`
        : `/signup?${queryString}`;
    }
  };

  switch (product) {
    case Product.TextApp:
      copy = 'Bring all your customer service tools into one platform';
      break;
    case Product.LiveChat:
      copy = 'Connect with customers';
      break;
    case Product.HelpDesk:
      copy = 'Support customers with tickets';
      break;
    case Product.KnowledgeBase:
      copy = 'Improve support with self-service';
      break;
    case Product.ChatBot:
      copy = 'Automate customer service with AI';
      break;
    case Product.OpenWidget:
      copy = 'Enhance websites with widgets';
      break;
    case Product.Platform:
      copy = 'Build and monetize automation tools';
      break;
    case Product.PartnerProgram:
      copy = 'Perfect for solution providers';
      break;
    default:
      copy = '';
  }

  if (isFetchingProducts) {
    return null;
  }

  return (
    <Button
      type="button"
      kind="secondary"
      css={productCardButtonStyles({ fullWidth })}
      onClick={handleClick}
      data-testid={`trial-product-card-${productName}`}
    >
      <div css={productCardContainerStyles}>
        <div css={productLogoContainerStyles}>
          <Logo size="xlarge" product={product} />
        </div>
        <div css={productCopyContainerStyles}>
          <Heading size="sm">{getProductDisplayName(productName)}</Heading>
          <Text size="md">{copy}</Text>
        </div>
      </div>
    </Button>
  );
};
